import { EXEC_PUSH_DUST } from './exec_parry'
import type { FxEvent } from './types'

// 主人公の受け身 (弾いた・防いだ時の姿と押され方・2026-10-21)。 前の版 = DEV ?ukemi=off。
//   10-20 の答え (docs/research/2026-10-20-ukemi-parry-guard-consult.md §4・§5) と 10-21 の手本の絵の承認
//   (答えるページ https://claude.ai/artifact/2DrVrKsywtnRh7HyAJAhP6 ・db answers/ukemi-art-1021):
//   軽いパリィ = 刀を立てて弾き、弾いた瞬間の光の下で受け身の姿へ切り替わる。 重いパリィ = 同じ形で押される量だけ大きい (腰を落とし、後ろ足に砂ぼこり)。
//   ガード = 腕と肩で押し込まれて半歩すべる (押される量は小さく・光らせない = 暗い火花は今の受けた光のまま・後ろ足に擦り跡)。
//   姿 = scripts/author_ukemi_from_parry.mjs が作る parry_ukemi / parry_ukemi_heavy / guard_hit。 記録 = docs/research/2026-10-21-ukemi-impl.md
export type UkemiMode = 'on' | 'off'
let mode: UkemiMode = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'on'
  return new URLSearchParams(window.location.search).get('ukemi') === 'off' ? 'off' : 'on'
})()
export function ukemiOn(): boolean { return mode === 'on' }
export function setUkemiMode(m: UkemiMode): void { mode = m }
// 10-21 の直し (重いパリィで 32px → 80px)。 前の版 = DEV ?ukemiTweak=off。 記録 = docs/research/2026-10-21-ukemi-tweak.md
let tweak = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return true
  return new URLSearchParams(window.location.search).get('ukemiTweak') !== 'off'
})()
export function ukemiTweakOn(): boolean { return tweak }
export function setUkemiTweakForTest(on: boolean): void { tweak = on }
// 10-22 の直し (ガードを軽い・重いの2種にし、重い技のガードは重いパリィより大きく下がる)。 前の版 = DEV ?guardHeavy=off。
//   ユーザー 10-22「重い攻撃をガードしたときは後ずさりの量を増やしてほしい」「重いパリィよりも後ろに下がるように」。 記録 = docs/research/2026-10-22-ukemi-guard-spark.md
let guardHeavy = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return true
  return new URLSearchParams(window.location.search).get('guardHeavy') !== 'off'
})()
export function guardHeavyOn(): boolean { return guardHeavy }
export function setGuardHeavyForTest(on: boolean): void { guardHeavy = on }
// 10-22 夕の直し (ガードの後ずさりを大きく)。 ユーザー 10-22「ガードの時はもう少し下がらせてもいいかも」。
//   重いガード 120 → 180px・軽いガード 10 → 16px (軽いパリィ 12 より少し大きく = 弾けず受け止めた分だけ押し負ける・重い方と同じ向き)。
//   10-22 夜 ユーザー「150でお願いします」→ 既定 150 (重い 150・軽い 14)。 比べる案 = DEV ?guardPush=150|180|210 (既定 150)・前の版 = ?guardPush=old (10 / 120)。 記録 = docs/research/2026-10-22-guard-push-blade-slide.md
export const GUARD_PUSH_PRESETS = {
  old: { guard: 10, guardHeavy: 120 },
  '150': { guard: 14, guardHeavy: 150 },
  '180': { guard: 16, guardHeavy: 180 },
  '210': { guard: 18, guardHeavy: 210 },
} as const
export type GuardPushPreset = keyof typeof GUARD_PUSH_PRESETS
export const GUARD_PUSH_DEFAULT: GuardPushPreset = '150'
let guardPush: GuardPushPreset = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return GUARD_PUSH_DEFAULT
  const q = new URLSearchParams(window.location.search).get('guardPush')
  return q !== null && q in GUARD_PUSH_PRESETS ? q as GuardPushPreset : GUARD_PUSH_DEFAULT
})()
export function setGuardPushForTest(p: GuardPushPreset): void { guardPush = p }

export const UKEMI = {
  /** これ以上の技の重さ (parryIntensity) は重いパリィ (src/parry_pitch.ts weightTier の heavy と同じ線)。 */
  heavyWeight: 0.75,
  /** 本当に後ろへ動く量 (px・knockbackRemainingPx)。 ガードは小さく (10-21「押される量を小さく」)。
   *  重いは 10-21 ユーザー「重いパリィはもう少しプレイヤーが後ずさりして欲しい」→「2.5倍（約80px）」(前 32px = heavyBefore)。
   *  guardHeavy = 重い技をガードした時 (10-22・重いパリィ 80px より大きく = 弾けず受け止めた分だけ押し負ける)。
   *  ガードの2つは既定の値 (配布版)。 実際に使うのは ukemiGuardPushPx (DEV の案の切り替えを読む)。 */
  pushPx: { light: 12, heavy: 80, ...GUARD_PUSH_PRESETS[GUARD_PUSH_DEFAULT] },
  heavyBefore: 32,
  anim: { light: 'parry_ukemi', heavy: 'parry_ukemi_heavy', guard: 'guard_hit' },
  /** 姿の尺 (秒)。 scripts/author_ukemi_from_parry.mjs と同じ (tests/ukemi.test.ts が固定)。 */
  parrySec: 0.62, guardSec: 0.36,
  /** 光で体を隠す (秒・弾いてから)。 入り・保ち・抜け。 この間に受け身の姿へ切り替わる (姿になるのは 0.06 秒)。 */
  cover: { inEnd: 0.03, holdEnd: 0.1, outEnd: 0.17, light: 0.8, heavy: 0.92 },
} as const

export type UkemiKind = 'light' | 'heavy'
export function ukemiKind(weight: number | undefined): UkemiKind {
  return (weight ?? 0) >= UKEMI.heavyWeight ? 'heavy' : 'light'
}
export function ukemiParryPushPx(weight: number | undefined): number {
  const kind = ukemiKind(weight)
  return kind === 'heavy' && !tweak ? UKEMI.heavyBefore : UKEMI.pushPx[kind]
}

export type UkemiGuardKind = 'guard' | 'guardHeavy'
/** ガードの種類 (重さの線はパリィと同じ)。 前の版 (?guardHeavy=off) は全部 guard。 */
export function ukemiGuardKind(weight: number | undefined): UkemiGuardKind {
  return guardHeavy && ukemiKind(weight) === 'heavy' ? 'guardHeavy' : 'guard'
}

/** ガードで下がる量 (px)。 */
export function ukemiGuardPushPx(kind: UkemiGuardKind): number {
  return GUARD_PUSH_PRESETS[guardPush][kind]
}

/** 光で体を覆う濃さ (0〜1)。 弾いてからの秒 (止めの間も数える)。 */
export function ukemiCoverAlpha(since: number, kind: UkemiKind): number {
  const C = UKEMI.cover
  const peak = C[kind]
  if (since < 0 || since >= C.outEnd) return 0
  if (since < C.inEnd) return peak * (since / C.inEnd)
  if (since < C.holdEnd) return peak
  const k = (since - C.holdEnd) / (C.outEnd - C.holdEnd)
  return peak * (1 - k) * (1 - k)
}

// 止めの間も進む時計 (src/parry_kesaki_fx.ts kesakiClock と同じ考え方。 止めの間は state.time が進まない)。
const stopSeen = new WeakMap<object, number>()
/** 出来事からの秒 (止めの間も数える)。 */
export function ukemiSince(ev: { t: number }, now: number, hitStopRemaining: number): number {
  const age = Math.max(0, now - ev.t)
  if (age > 0 || hitStopRemaining <= 0) return (stopSeen.get(ev) ?? 0) + age
  const seen = Math.max(stopSeen.get(ev) ?? 0, hitStopRemaining)
  stopSeen.set(ev, seen)
  return seen - hitStopRemaining
}

/** 重いパリィの後ろ足の砂ぼこり・ガードの後ろ足の擦り跡 (描くだけ・絵は騎士の踏み込みの土煙 src/knight_lunge_dust.ts)。 */
export function ukemiDustFx(kind: UkemiKind | UkemiGuardKind, x: number, y: number, away: number, t: number): FxEvent[] {
  const back = x + away * EXEC_PUSH_DUST.backFootPx
  if (kind === 'heavy') return [{ kind: 'knight_lunge_dust', variant: 'kick', x: back, y, t, dir: away, amount: 0.55 }]
  if (kind === 'guard') return [{ kind: 'knight_lunge_dust', variant: 'scuff', x: back, x2: back + away * ukemiGuardPushPx('guard'), y, t, dir: away, amount: 0.35 }]
  // 重いガード: 踏ん張った後ろ足の土煙 (滑った跡は knockSkidFx が下がった長さだけ伸ばす)。
  if (kind === 'guardHeavy') return [{ kind: 'knight_lunge_dust', variant: 'kick', x: back, y, t, dir: away, amount: 0.65 }]
  return []
}
